import { supabase } from '../lib/supabase';
import { logEvent } from './loggingService';
import { buildOptimizationPrompt, OptimizationPreferences } from '../utils/promptBuilder';
import { transformGroqResponseToOptimizationResult } from '../utils/responseTransformer';
import { fixUtf8Mojibake } from '../utils/pdfTextSanitizer';
import { detectExperienceLevel } from '../utils/resumeAnalysisUtils';
import { calculateInitialJobMatchScore } from '../utils/scoreCalculations';
import { saveComprehensiveOptimization } from './optimizationService';
import type { OptimizationResult, JobData, OptimizationChunk } from '../types/optimization';
import { extractJDStructure, analyzeGaps } from '../utils/jdAnalysis';

// ============================================================================
// OPTIMIZATION CHUNK MANAGEMENT
// ============================================================================

export function initializeOptimizationChunks(): OptimizationChunk[] {
  return [
    {
      id: 'analysis',
      name: 'Resume Analysis',
      status: 'pending',
      progress: 0,
      subChunks: [
        { id: 'parse', name: 'Parsing resume content', status: 'pending', progress: 0 },
        { id: 'validate', name: 'Validating structure', status: 'pending', progress: 0 },
        { id: 'extract', name: 'Extracting key information', status: 'pending', progress: 0 }
      ]
    },
    {
      id: 'job-processing',
      name: 'Job Requirements Processing',
      status: 'pending',
      progress: 0,
      subChunks: [
        { id: 'fetch', name: 'Fetching job details', status: 'pending', progress: 0 },
        { id: 'analyze', name: 'Analyzing requirements', status: 'pending', progress: 0 },
        { id: 'match', name: 'Calculating initial match', status: 'pending', progress: 0 }
      ]
    },
    {
      id: 'optimization',
      name: 'AI Optimization',
      status: 'pending',
      progress: 0,
      subChunks: [
        { id: 'generate', name: 'Generating optimized content', status: 'pending', progress: 0 },
        { id: 'enhance', name: 'Enhancing for ATS compatibility', status: 'pending', progress: 0 },
        { id: 'finalize', name: 'Finalizing optimized resume', status: 'pending', progress: 0 }
      ]
    }
  ];
}

export function calculateOverallProgress(chunks: OptimizationChunk[]): number {
  if (chunks.length === 0) return 0;
  
  const totalProgress = chunks.reduce((sum, chunk) => sum + chunk.progress, 0);
  return Math.round(totalProgress / chunks.length);
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ============================================================================
// CHUNK UPDATE UTILITIES
// ============================================================================

function updateChunkProgress(
  chunks: OptimizationChunk[],
  chunkId: string,
  subChunkId: string,
  status: 'pending' | 'processing' | 'completed' | 'failed',
  progress: number
): OptimizationChunk[] {
  return chunks.map(chunk => {
    if (chunk.id === chunkId) {
      const updatedSubChunks = chunk.subChunks.map(subChunk => {
        if (subChunk.id === subChunkId) {
          return { ...subChunk, status, progress };
        }
        return subChunk;
      });
      
      // Calculate chunk progress based on sub-chunks
      const chunkProgress = Math.round(
        updatedSubChunks.reduce((sum, sub) => sum + sub.progress, 0) / updatedSubChunks.length
      );
      
      // Update chunk status based on sub-chunks
      let chunkStatus: 'pending' | 'processing' | 'completed' | 'failed' = 'pending';
      if (updatedSubChunks.every(sub => sub.status === 'completed')) {
        chunkStatus = 'completed';
      } else if (updatedSubChunks.some(sub => sub.status === 'processing')) {
        chunkStatus = 'processing';
      } else if (updatedSubChunks.some(sub => sub.status === 'failed')) {
        chunkStatus = 'failed';
      }
      
      return {
        ...chunk,
        subChunks: updatedSubChunks,
        progress: chunkProgress,
        status: chunkStatus
      };
    }
    return chunk;
  });
}

function simulateProgressUpdate(
  updateChunks: (updater: (chunks: OptimizationChunk[]) => OptimizationChunk[]) => void,
  chunkId: string,
  subChunkId: string,
  duration: number,
  intervalsRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
): Promise<void> {
  return new Promise((resolve) => {
    const steps = 10;
    const stepDuration = duration / steps;
    let currentStep = 0;
    
    // Set to processing immediately
    updateChunks(chunks => updateChunkProgress(chunks, chunkId, subChunkId, 'processing', 0));
    
    const interval = setInterval(() => {
      currentStep++;
      const progress = Math.round((currentStep / steps) * 100);
      
      updateChunks(chunks => updateChunkProgress(
        chunks, 
        chunkId, 
        subChunkId, 
        currentStep === steps ? 'completed' : 'processing', 
        progress
      ));
      
      if (currentStep >= steps) {
        clearInterval(interval);
        intervalsRef.current.delete(interval);
        resolve();
      }
    }, stepDuration);
    
    intervalsRef.current.set(interval, interval);
  });
}

// ============================================================================
// MAIN OPTIMIZATION FUNCTION
// ============================================================================

export async function performOptimization(
  resumeText: string,
  jobData: JobData,
  resumeId: string | null,
  updateChunks: (updater: (chunks: OptimizationChunk[]) => OptimizationChunk[]) => void,
  intervalsRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>,
  selectedModel: string = 'groq-llama-3.3-70b-versatile',
  preferences?: OptimizationPreferences
): Promise<OptimizationResult> {
  const startTime = Date.now();
  
  try {
    console.log('[OptimizationService] Starting optimization process for:', jobData.title);
    
    // Validate inputs first
    validateOptimizationInput(resumeText, jobData);
    
    // Get user session (optional - guest users can optimize too)
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session) {
      console.log('[OptimizationService] User authenticated - full optimization');
    } else {
      console.log('[OptimizationService] Guest user - proceeding with optimization');
    }

    // ========================================================================
    // PHASE 1: RESUME ANALYSIS
    // ========================================================================
    
    console.log('[OptimizationService] Phase 1: Resume Analysis');
    
    // Simulate parsing
    await simulateProgressUpdate(updateChunks, 'analysis', 'parse', 800, intervalsRef);
    
    // Validate resume content
    if (!resumeText || resumeText.trim().length < 100) {
      throw new Error('Resume content is too short or empty');
    }
    
    await simulateProgressUpdate(updateChunks, 'analysis', 'validate', 600, intervalsRef);
    
    // Extract key information
    const experienceLevel = detectExperienceLevel(resumeText, jobData);
    console.log('[OptimizationService] Detected experience level:', experienceLevel);
    
    await simulateProgressUpdate(updateChunks, 'analysis', 'extract', 700, intervalsRef);

    // ========================================================================
    // PHASE 2: JOB PROCESSING
    // ========================================================================
    
    console.log('[OptimizationService] Phase 2: Job Requirements Processing');
    
    await simulateProgressUpdate(updateChunks, 'job-processing', 'fetch', 500, intervalsRef);
    
    // Validate job data (allow pasted description only: title/company can be placeholders)
    const hasJobDescription = !!(jobData.description && jobData.description.trim().length >= 50);
    const hasTitle = !!(jobData.title && String(jobData.title).trim());
    const hasCompany = !!(jobData.company && String(jobData.company).trim());
    if (!hasTitle && !hasJobDescription) {
      throw new Error('Invalid job data: missing job title or pasted job description');
    }
    if (!hasCompany && !hasJobDescription) {
      throw new Error('Invalid job data: missing company or pasted job description');
    }
    
    await simulateProgressUpdate(updateChunks, 'job-processing', 'analyze', 800, intervalsRef);

    // ── Gap Analysis: structure JD + classify resume-vs-JD skill buckets ──────
    let gapAnalysis = preferences?.gapAnalysis;
    if (!gapAnalysis && (jobData.description || (jobData.skills?.length ?? 0) > 0)) {
      try {
        const jdStructure = extractJDStructure(jobData);
        gapAnalysis = analyzeGaps(resumeText, jdStructure);
        console.log('[OptimizationService] Gap analysis complete:', {
          strong: gapAnalysis.strongMatches.length,
          weak: gapAnalysis.weakMatches.length,
          implied: gapAnalysis.impliedMatches.length,
          missing: gapAnalysis.missingSkills.length,
          weakBullets: gapAnalysis.weakBullets.length,
          coverageScore: gapAnalysis.coverageScore,
        });
      } catch (err) {
        console.warn('[OptimizationService] Gap analysis failed (non-fatal):', err);
      }
    }

    // Calculate initial job match score
    const initialJobMatchScore = calculateInitialJobMatchScore(resumeText, jobData);
    console.log('[OptimizationService] Initial job match score:', initialJobMatchScore);
    
    await simulateProgressUpdate(updateChunks, 'job-processing', 'match', 600, intervalsRef);

    // ========================================================================
    // PHASE 3: AI OPTIMIZATION (SINGLE CALL)
    // ========================================================================
    
    console.log('[OptimizationService] Phase 3: AI Optimization - Single LLM Call');
    
    // Start optimization chunk
    updateChunks(chunks => updateChunkProgress(chunks, 'optimization', 'generate', 'processing', 0));
    
    // Build the optimization prompt with gap analysis injected
    const prefsWithGap = gapAnalysis ? { ...(preferences || {}), gapAnalysis } : preferences;
    const optimizationPrompt = buildOptimizationPrompt(resumeText, jobData, experienceLevel, prefsWithGap);
    console.log('[OptimizationService] Built optimization prompt, length:', optimizationPrompt.length);
    console.log('[OptimizationService] User preferences:', {
      mode: preferences?.mode || 'balanced',
      tone: preferences?.writingTone || 'professional',
      detail: preferences?.detailLevel || 'detailed',
      focusAreas: preferences?.focusAreas || ['all']
    });
    
    // CRITICAL: Single LLM call to generate complete resume
    console.log(`[OptimizationService] Making single LLM call with model: ${selectedModel}...`);
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }
    
   // const apiUrl = `${supabaseUrl}/functions/v1/groq-llm`;
    const apiUrl = `${supabaseUrl}/functions/v1/optimize-resume`;
    
    let response;
    try {
      // Transform preferences to match edge function format
      const transformedPreferences = preferences ? {
        tone: preferences.writingTone || 'professional', // Map writingTone -> tone
        verbosity: preferences.detailLevel || 'detailed', // Map detailLevel -> verbosity
        strictATS: preferences.mode === 'aggressive', // Map mode -> strictATS
        enhanceKeywords: preferences.enhanceKeywords ?? true,
        improveFormatting: preferences.improveFormatting ?? true,
        enhanceContent: preferences.enhanceContent ?? true,
        improveStructure: preferences.improveStructure ?? true,
        mode: preferences.mode, // Keep original mode for reference
        focusAreas: preferences.focusAreas || [] // Keep original focusAreas
      } : undefined;

      console.log('[OptimizationService] Transformed preferences:', {
        original: preferences,
        transformed: transformedPreferences
      });

      // Build headers - include auth token only if session exists
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      
      const useChunkedPipeline = preferences?.useChunkedPipeline ?? import.meta.env.VITE_USE_CHUNKED_OPTIMIZATION === 'true';
      response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: selectedModel,
          prompt: useChunkedPipeline ? undefined : optimizationPrompt,
          resumeText: resumeText,
          jobData: jobData,
          experienceLevel: experienceLevel,
          resumeId: resumeId || undefined,
          jobId: jobData.jobId || undefined,
          preferences: transformedPreferences,
          bypassCredits: import.meta.env.VITE_BYPASS_CREDIT_SYSTEM === 'true',
          useChunkedPipeline: useChunkedPipeline,
        }),
        signal: AbortSignal.timeout(useChunkedPipeline ? 180000 : 120000)
      });
    } catch (fetchError) {
      console.error('[OptimizationService] ERROR: Network error calling LLM API:', fetchError);
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Failed to connect to optimization service'}`);
    }

    if (!response.ok) {
      let errorMessage = 'LLM optimization failed';
      try {
        const errorData = await response.json();
        console.error('[OptimizationService] ERROR: LLM API error details:', errorData);
        errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      } catch (parseError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      console.error('[OptimizationService] ERROR: LLM API error response:', { status: response.status, statusText: response.statusText });
      throw new Error(errorMessage);
    }

    let llmResponse;
    try {
      llmResponse = await response.json();
      console.log('[OptimizationService] LLM response structure:', {
        hasOptimizedContent: !!llmResponse.optimizedContent,
        optimizedContentLength: llmResponse.optimizedContent?.length || 0,
        responseKeys: Object.keys(llmResponse)
      });
    } catch (parseError) {
      console.error('[OptimizationService] ERROR: Failed to parse LLM response:', parseError);
      throw new Error('Invalid response from optimization service');
    }
    
    // Update progress for generation
    updateChunks(chunks => updateChunkProgress(chunks, 'optimization', 'generate', 'completed', 100));
    
    // Extract optimized content (backend returns 'optimizedContent', map it to 'optimizedResume')
    let optimizedResume = llmResponse.optimizedContent || llmResponse.optimizedResume;
    if (optimizedResume) optimizedResume = fixUtf8Mojibake(optimizedResume);
    
    // Validate LLM response
    if (!optimizedResume || optimizedResume.trim().length < 200) {
      console.error('[OptimizationService] ERROR: Invalid LLM response:', {
        hasOptimizedContent: !!llmResponse.optimizedContent,
        hasOptimizedResume: !!llmResponse.optimizedResume,
        optimizedContentLength: llmResponse.optimizedContent?.length || 0,
        fullResponse: llmResponse
      });
      throw new Error(`Incomplete resume generated: ${optimizedResume?.length || 0} characters. The AI service may be experiencing issues.`);
    }
    
    await simulateProgressUpdate(updateChunks, 'optimization', 'enhance', 800, intervalsRef);
    
    // ========================================================================
    // PHASE 4: FINALIZATION
    // ========================================================================
    
    console.log('[OptimizationService] Phase 4: Finalizing optimization result');
    
    await simulateProgressUpdate(updateChunks, 'optimization', 'finalize', 600, intervalsRef);
    
    // Transform the response to optimization result
    let optimizationResult = transformGroqResponseToOptimizationResult(
      optimizedResume,
      resumeText,
      initialJobMatchScore,
      {
        ...jobData,
        userId: session?.user?.id || 'guest',
        resumeId: resumeId
      },
      Date.now() - startTime
    );

    // Use API's beforeScores/afterScores when present (formatting, keywords, structure) so Key Improvements are dynamic
    if (llmResponse.result?.beforeScores && llmResponse.result?.afterScores) {
      optimizationResult = {
        ...optimizationResult,
        beforeScores: llmResponse.result.beforeScores,
        afterScores: llmResponse.result.afterScores
      };
    }
    // Attach pre-parsed sections from LLM JSON — eliminates regex re-parsing in the UI
    const llmSections = llmResponse.result?.sections ?? llmResponse.sections;
    if (Array.isArray(llmSections) && llmSections.length > 0) {
      (optimizationResult as any).sections = llmSections;
    }
    // Attach gap analysis so downstream components (e.g. ResultsStep) can surface insights
    if (gapAnalysis) {
      (optimizationResult as any).gapAnalysis = gapAnalysis;
    }
    // Use API-returned resume_id and job_id (normalized UUIDs) so Save to History works
    if (llmResponse.resume_id) (optimizationResult as any).resumeId = llmResponse.resume_id;
    if (llmResponse.job_id) (optimizationResult as any).jobId = llmResponse.job_id;

    console.log('[OptimizationService] Optimization result created:', {
      optimizationId: optimizationResult.optimization_id,
      originalScore: optimizationResult.originalScore,
      optimizedScore: optimizationResult.optimizedScore,
      improvementsCount: optimizationResult.improvements?.length || 0,
      optimizedResumeLength: optimizationResult.optimizedResume?.length || 0
    });
    
    // ========================================================================
    // PHASE 5: SAVE TO DATABASE
    // ========================================================================
    
    console.log('[OptimizationService] Saving optimization to database...');
    
    try {
      if (resumeId && jobData.jobId) {
        console.log('[OptimizationService] Attempting to save with resumeId and jobId:', { resumeId, jobId: jobData.jobId });
        const savedResult = await saveComprehensiveOptimization(
          resumeId,
          jobData.jobId,
          optimizationResult
        );
        
        console.log('[OptimizationService] Optimization saved to database:', savedResult.optimization_id);
        
        // Return the saved result with database IDs
        return {
          ...optimizationResult,
          optimization_id: savedResult.optimization_id,
          link_id: savedResult.link_id,
          created_at: savedResult.created_at
        };
      } else {
        console.warn('[OptimizationService] WARNING: Skipping database save - missing resumeId or jobId:', { resumeId, jobId: jobData.jobId });
      }
    } catch (saveError) {
      console.warn('[OptimizationService] WARNING: Failed to save to database (non-critical):', saveError);
      // Continue with in-memory result - don't fail the entire optimization
    }
    
    console.log('[OptimizationService] Optimization completed successfully');
    return optimizationResult;
    
  } catch (error) {
    console.error('[OptimizationService] ERROR: Optimization failed:', error);
    
    // Update all chunks to failed state
    updateChunks(chunks => chunks.map(chunk => ({
      ...chunk,
      status: 'failed',
      progress: 0,
      subChunks: chunk.subChunks.map(sub => ({
        ...sub,
        status: 'failed',
        progress: 0
      }))
    })));
    
    // Clear all intervals
    intervalsRef.current.forEach(interval => clearInterval(interval));
    intervalsRef.current.clear();
    
    await logEvent({
      level: 'error',
      action: 'optimization_failed',
      details: {
        jobTitle: jobData.title,
        error: error instanceof Error ? error.message : String(error),
        processingTime: Date.now() - startTime
      },
      error: error instanceof Error ? error : new Error(String(error))
    });
    
    throw error;
  }
}

// ============================================================================
// MULTI-SEQUENCE OPTIMIZATION UTILITIES
// ============================================================================

export async function performBatchOptimization(
  resumeText: string,
  jobDataArray: JobData[],
  resumeId: string | null,
  onSequenceUpdate: (sequenceId: string, result: OptimizationResult | null, error: string | null) => void,
  onProgressUpdate: (sequenceId: string, progress: number) => void,
  deductCredits?: (amount: number) => Promise<boolean>
): Promise<OptimizationResult[]> {
  const results: OptimizationResult[] = [];
  const startTime = Date.now();
  
  console.log('[OptimizationService] Starting batch optimization for', jobDataArray.length, 'jobs');
  
  try {
    // Process each job sequentially to avoid LLM conflicts
    for (let i = 0; i < jobDataArray.length; i++) {
      const jobData = jobDataArray[i];
      const sequenceId = `sequence-${i}`;
      
      console.log(`[OptimizationService] Processing sequence ${i + 1}/${jobDataArray.length}: ${jobData.title}`);
      
      try {
        // Create a simple progress updater for this sequence
        const updateSequenceProgress = (progress: number) => {
          onProgressUpdate?.(sequenceId, progress);
        };
        
        // Create chunks updater that tracks progress
        let sequenceProgress = 0;
        const updateChunks = (updater: (chunks: OptimizationChunk[]) => OptimizationChunk[]) => {
          const chunks = initializeOptimizationChunks();
          const updatedChunks = updater(chunks);
          
          // Calculate progress from chunks
          const newProgress = calculateOverallProgress(updatedChunks);
          if (newProgress !== sequenceProgress) {
            sequenceProgress = newProgress;
            updateSequenceProgress(newProgress);
          }
        };
        
        // Create a dummy intervals ref for this sequence
        const sequenceIntervalsRef = { current: new Map<string, NodeJS.Timeout>() };
        
        // Perform optimization for this specific job
        const result = await performOptimization(
          resumeText,
          {
            ...jobData,
            jobIndex: i,
            userId: (await supabase.auth.getUser()).data.user?.id || '',
            resumeId: resumeId
          },
          resumeId,
          updateChunks,
          sequenceIntervalsRef
        );
        
        console.log(`[OptimizationService] Sequence ${i + 1} completed:`, {
          jobTitle: result.jobTitle,
          optimizedScore: result.optimizedScore,
          optimizedResumeLength: result.optimizedResume?.length || 0
        });
        
        // Validate result completeness
        if (!result.optimizedResume || result.optimizedResume.trim().length < 200) {
          throw new Error(`Incomplete optimization result: ${result.optimizedResume?.length || 0} characters`);
        }
        
        results.push(result);
        onSequenceUpdate(sequenceId, result, null);
        
        // Clear intervals for this sequence
        sequenceIntervalsRef.current.forEach(interval => clearInterval(interval));
        sequenceIntervalsRef.current.clear();
        
        // Add delay between sequences to prevent LLM rate limiting
        if (i < jobDataArray.length - 1) {
          console.log('[OptimizationService] Waiting 2 seconds before next sequence...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        
      } catch (sequenceError) {
        console.error(`[OptimizationService] ERROR: Sequence ${i + 1} failed:`, sequenceError);
        onSequenceUpdate(sequenceId, null, sequenceError instanceof Error ? sequenceError.message : String(sequenceError));
        
        // Continue with next sequence even if this one fails
        continue;
      }
    }
    
    console.log('[OptimizationService] Batch optimization completed:', {
      totalJobs: jobDataArray.length,
      successfulOptimizations: results.length,
      failedOptimizations: jobDataArray.length - results.length,
      totalTime: Date.now() - startTime
    });
    
    await logEvent({
      level: 'info',
      action: 'batch_optimization_completed',
      details: {
        totalJobs: jobDataArray.length,
        successfulOptimizations: results.length,
        failedOptimizations: jobDataArray.length - results.length,
        totalTime: Date.now() - startTime,
        jobTitles: jobDataArray.map(job => job.title)
      }
    });
    
    return results;
    
  } catch (error) {
    console.error('[OptimizationService] ERROR: Batch optimization failed:', error);
    
    await logEvent({
      level: 'error',
      action: 'batch_optimization_failed',
      details: {
        totalJobs: jobDataArray.length,
        completedJobs: results.length,
        error: error instanceof Error ? error.message : String(error),
        totalTime: Date.now() - startTime
      },
      error: error instanceof Error ? error : new Error(String(error))
    });
    
    throw error;
  }
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

export function validateOptimizationInput(resumeText: string, jobData: JobData): void {
  // Validate resume text
  if (!resumeText || typeof resumeText !== 'string') {
    throw new Error('Invalid resume text provided');
  }
  
  if (resumeText.trim().length < 100) {
    throw new Error('Resume text is too short (minimum 100 characters required)');
  }
  
  // Validate job data
  if (!jobData || typeof jobData !== 'object') {
    throw new Error('Invalid job data provided');
  }
  const hasDescription = !!(jobData.description && jobData.description.trim().length >= 50);
  const hasTitle = !!(jobData.title && jobData.title.trim());
  const hasCompany = !!(jobData.company && jobData.company.trim());
  if (!hasTitle && !hasDescription) {
    throw new Error('Job data must include a job title or a pasted job description');
  }
  if (!hasCompany && !hasDescription) {
    throw new Error('Job data must include company or a pasted job description');
  }
  console.log('[OptimizationService] Input validation passed');
}

export function validateOptimizationResult(result: OptimizationResult): void {
  // Validate required fields
  if (!result.optimizedResume || result.optimizedResume.trim().length < 200) {
    throw new Error(`Optimization result incomplete: ${result.optimizedResume?.length || 0} characters`);
  }
  
  if (typeof result.originalScore !== 'number' || isNaN(result.originalScore)) {
    throw new Error('Invalid original score in optimization result');
  }
  
  if (typeof result.optimizedScore !== 'number' || isNaN(result.optimizedScore)) {
    throw new Error('Invalid optimized score in optimization result');
  }
  
  console.log('[OptimizationService] Optimization result validation passed');
}
